// Driver schedule — workforce-planning grid.
//
// Two-week (Mon..Fri × 2) view with drivers down the rows and days
// across the columns. Cell content is derived from `routes` today:
// a driver is "scheduled at pharmacy X on date D" iff they own a
// non-cancelled route at that pharmacy on that date. Multi-route
// days stack pharmacy chips. No-routes days render "Off".
//
// Deferred (schema work, not stubs): an explicit `driver_shifts`
// table is required before we can model dispatcher-modified
// (blue outline) vs driver-requested (orange outline) cells; before
// then the cells reflect actual route assignments only. Time-off
// requests, the "+" inline cell editor, and the Master Schedule
// / Rules Audit surfaces are also gated on that schema work.

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as Sentry from '@sentry/react';
import type { LynrxSupabaseClient } from '@lynrx/shared';
import { useAuth } from '../lib/auth/auth-context';
import { requireUserType } from '../lib/auth/guards';
import { supabase as defaultClient } from '../lib/supabase';
import { Alert } from '../components/ui/Alert';
import { useSetPageHeader } from '../components/layout/PageHeaderContext';
import { IconChevronLeft, IconChevronRight } from '../components/ui/Icon';
import {
  hashPharmacyColor,
  shortCodeFromName,
  type PharmacyColor,
} from './_authenticated.dispatch';

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

type ScheduleSearch = { start: string };

export const Route = createFileRoute('/_authenticated/schedule')({
  beforeLoad: async () => {
    await requireUserType({ allowed: ['dispatcher', 'pharmacy_admin'] });
  },
  validateSearch: (search: Record<string, unknown>): ScheduleSearch => {
    const raw = search['start'];
    const start =
      typeof raw === 'string' && DATE_RE.test(raw) ? raw : mondayOf(todayIso());
    return { start };
  },
  component: SchedulePage,
});

function SchedulePage(): JSX.Element {
  useSetPageHeader({ title: 'Schedule', hideTopBar: true });
  const { start } = Route.useSearch();
  const navigate = useNavigate();
  const setStart = useCallback(
    (next: string) => {
      void navigate({ to: '/schedule', search: { start: next }, replace: true });
    },
    [navigate],
  );
  return <ScheduleView start={start} onStartChange={setStart} />;
}

// ---------------------------------------------------------------------------
// Date helpers — local-calendar terms throughout.

function todayIso(): string {
  return toIso(new Date());
}

function toIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseIso(iso: string): Date {
  const parts = iso.split('-');
  return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
}

function addDays(iso: string, n: number): string {
  const d = parseIso(iso);
  return toIso(new Date(d.getFullYear(), d.getMonth(), d.getDate() + n));
}

export function mondayOf(iso: string): string {
  const d = parseIso(iso);
  const dow = d.getDay();
  const offset = dow === 0 ? -6 : 1 - dow;
  return addDays(iso, offset);
}

// Build the 10 weekday columns (Mon..Fri × 2 weeks) starting at `start`
// (which is expected to be the Monday of week 1).
export function buildDayColumns(start: string): string[] {
  const monday = mondayOf(start);
  const result: string[] = [];
  for (let week = 0; week < 2; week++) {
    for (let i = 0; i < 5; i++) {
      result.push(addDays(monday, week * 7 + i));
    }
  }
  return result;
}

function shortDayLabel(iso: string): string {
  return parseIso(iso).toLocaleDateString(undefined, { weekday: 'short' }).toUpperCase();
}

function shortDateLabel(iso: string): string {
  const d = parseIso(iso);
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

function rangeLabel(start: string, end: string): string {
  const s = parseIso(start);
  const e = parseIso(end);
  const sFmt = s.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const eFmt = e.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  return `${sFmt} – ${eFmt}`;
}

// ---------------------------------------------------------------------------
// Data shapes.

// Shifts are the source of truth for cell rendering — separate from
// routes. `pharmacy_id IS NULL` = explicitly "off"; absence of a row
// = unscheduled (also rendered "Off"). The migration backfills any
// existing route assignments into shifts so the page doesn't go blank
// on first apply.
type ShiftRow = {
  id: string;
  driver_id: string;
  shift_date: string;
  pharmacy_id: string | null;
};

// Routes are kept as a parallel query — purely for the day-header
// "stops today" count and the top-strip "Stops today" stat. Not used
// for cell rendering anymore.
type RouteRow = {
  id: string;
  driver_id: string | null;
  planned_for: string;
  route_status: string;
  pharmacy_id: string | null;
  stops: { id: string }[];
};

type DriverRow = {
  id: string;
  user: { full_name: string | null; email: string } | null;
};

type PharmacyOption = {
  id: string;
  name: string;
  shortCode: string;
  colorToken: PharmacyColor;
};

type CellRouteRef = {
  routeId: string; // shift id (kept name for the type to fit existing component contract)
  pharmacyId: string;
  shortCode: string;
  colorToken: PharmacyColor;
};

// Statuses that count as "scheduled" for the stops-today stat — cancelled
// routes don't represent live workload.
const ACTIVE_STATUSES = [
  'draft',
  'assigned',
  'in_progress',
  'completed',
] satisfies ReadonlyArray<
  'draft' | 'assigned' | 'in_progress' | 'completed' | 'cancelled' | 'published'
>;

// ---------------------------------------------------------------------------
// View.

type ViewProps = {
  start: string;
  onStartChange: (next: string) => void;
  client?: LynrxSupabaseClient;
};

export function ScheduleView({
  start,
  onStartChange,
  client = defaultClient,
}: ViewProps): JSX.Element {
  const { session, isReady } = useAuth();
  const [shifts, setShifts] = useState<ShiftRow[] | null>(null);
  const [routes, setRoutes] = useState<RouteRow[] | null>(null);
  const [drivers, setDrivers] = useState<DriverRow[] | null>(null);
  const [pharmacies, setPharmacies] = useState<PharmacyOption[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savingCell, setSavingCell] = useState<string | null>(null);

  const days = useMemo(() => buildDayColumns(start), [start]);
  const weekStart = days[0] ?? start;
  const weekEnd = days[days.length - 1] ?? start;

  // Shifts table — driver/date → pharmacy_id (or NULL for "off"). The
  // table + RPCs land in migration 20260629210000_driver_shifts.sql;
  // the type assertions stay until `pnpm db:types` regenerates after
  // that migration is applied.
  // True when the driver_shifts table is missing on the current
  // Supabase instance — degrades cell-edit + clears the error banner.
  // Set by fetchShifts on PG 42P01 (undefined_table). Clears once the
  // migration is applied.
  const [migrationPending, setMigrationPending] = useState(false);

  const fetchShifts = useCallback(
    async (firstDay: string, lastDay: string) => {
      if (!session) return;
      setError(null);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error: err } = await (client as any)
        .from('driver_shifts')
        .select('id, driver_id, shift_date, pharmacy_id')
        .is('deleted_at', null)
        .gte('shift_date', firstDay)
        .lte('shift_date', lastDay);
      if (err) {
        // Degrade silently when the migration isn't applied yet —
        // PostgREST surfaces PG 42P01 (undefined_table) as a 404 with
        // the code in `err.code` and the table name in `err.message`.
        const code = (err as { code?: string }).code;
        const message = (err as { message?: string }).message ?? '';
        const isMissingTable =
          code === '42P01' ||
          /driver_shifts/i.test(message) && /(does not exist|not found|schema cache)/i.test(message);
        if (isMissingTable) {
          setMigrationPending(true);
          setShifts([]);
          return;
        }
        Sentry.captureException(err, { tags: { area: 'schedule.fetch_shifts' } });
        setError('Could not load the schedule window.');
        return;
      }
      setMigrationPending(false);
      setShifts((data ?? []) as unknown as ShiftRow[]);
    },
    [client, session],
  );

  // Routes — kept ONLY for the stops-per-day count in column headers
  // and the "Stops today" stat. Cell rendering is shift-driven.
  const fetchRoutes = useCallback(
    async (firstDay: string, lastDay: string) => {
      if (!session) return;
      const { data, error: err } = await client
        .from('routes')
        .select('id, driver_id, planned_for, route_status, pharmacy_id, stops(id)')
        .is('deleted_at', null)
        .gte('planned_for', firstDay)
        .lte('planned_for', lastDay)
        .in('route_status', ACTIVE_STATUSES);
      if (err) {
        Sentry.captureException(err, { tags: { area: 'schedule.fetch_routes' } });
        // Non-fatal — stops count just won't render.
        setRoutes([]);
        return;
      }
      setRoutes((data ?? []) as unknown as RouteRow[]);
    },
    [client, session],
  );

  const fetchDrivers = useCallback(async () => {
    if (!session) return;
    const { data, error: err } = await client
      .from('drivers')
      .select('id, user:users!drivers_id_fkey(full_name, email)')
      .is('deleted_at', null);
    if (err) {
      Sentry.captureException(err, { tags: { area: 'schedule.fetch_drivers' } });
      setDrivers([]);
      return;
    }
    setDrivers((data ?? []) as unknown as DriverRow[]);
  }, [client, session]);

  // Pharmacy picker options — RLS scopes to pharmacies the caller can
  // see. Used by the cell-click popover.
  const fetchPharmacies = useCallback(async () => {
    if (!session) return;
    const { data, error: err } = await client
      .from('pharmacies')
      .select('id, name')
      .is('deleted_at', null)
      .order('name');
    if (err) {
      Sentry.captureException(err, { tags: { area: 'schedule.fetch_pharmacies' } });
      setPharmacies([]);
      return;
    }
    const opts: PharmacyOption[] = (data ?? []).map((p) => ({
      id: p.id,
      name: p.name,
      shortCode: shortCodeFromName(p.name),
      colorToken: hashPharmacyColor(p.id),
    }));
    setPharmacies(opts);
  }, [client, session]);

  // Optimistic shift mutation: update local state immediately, fire
  // the RPC, refetch on completion (or revert on error).
  const setCellPharmacy = useCallback(
    async (driverId: string, shiftDate: string, pharmacyId: string | null) => {
      const cellKey = `${driverId}|${shiftDate}`;
      setSavingCell(cellKey);

      // Optimistic update: drop any existing live shift for this
      // (driver, date) from local state, then insert the synthetic
      // new one. Real id will land in the refetch.
      setShifts((prev) => {
        const without = (prev ?? []).filter(
          (s) => !(s.driver_id === driverId && s.shift_date === shiftDate),
        );
        return [
          ...without,
          {
            id: `optimistic-${cellKey}`,
            driver_id: driverId,
            shift_date: shiftDate,
            pharmacy_id: pharmacyId,
          },
        ];
      });

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error: err } = await (client as any).rpc('upsert_driver_shift', {
        p_driver_id: driverId,
        p_shift_date: shiftDate,
        p_pharmacy_id: pharmacyId,
      });
      setSavingCell(null);
      if (err) {
        Sentry.captureException(err, { tags: { area: 'schedule.upsert_shift' } });
        setError('Could not save shift. Refresh to recover.');
      }
      // Refetch either way — refetch gives the optimistic update a
      // real id and reconciles any error.
      await fetchShifts(weekStart, weekEnd);
    },
    [client, fetchShifts, weekStart, weekEnd],
  );

  useEffect(() => {
    if (!isReady || !session) return;
    void fetchShifts(weekStart, weekEnd);
    void fetchRoutes(weekStart, weekEnd);
  }, [isReady, session, fetchShifts, fetchRoutes, weekStart, weekEnd]);

  useEffect(() => {
    if (!isReady || !session) return;
    void fetchDrivers();
    void fetchPharmacies();
  }, [isReady, session, fetchDrivers, fetchPharmacies]);

  // Look up pharmacy name by id — sourced from the picker list which
  // is the canonical source. Falls back to the id for resilience.
  const pharmacyById = useMemo(() => {
    const m = new Map<string, PharmacyOption>();
    for (const p of pharmacies ?? []) m.set(p.id, p);
    return m;
  }, [pharmacies]);

  // Cells indexed by (driverId, shift_date) — pulled from `shifts`,
  // not routes. NULL pharmacy_id = explicit "off"; absent row = no
  // decision yet. Both render as "Off" but the audit trail
  // distinguishes them.
  const indexed = useMemo(() => {
    const byDriver = new Map<string, Map<string, CellRouteRef[]>>();
    const stopsPerDay = new Map<string, number>();
    const activeDriversToday = new Set<string>();
    const driversByPharmacyToday = new Map<string, Set<string>>();

    const today = todayIso();

    // Cells + today's pharmacy-split + active-today come from shifts.
    // A shift with pharmacy_id != null on `today` counts as active.
    // Explicit "off" (pharmacy_id === null) doesn't count.
    for (const s of shifts ?? []) {
      if (!s.pharmacy_id) continue; // explicit "off" stays empty in the cells + stats
      const ph = pharmacyById.get(s.pharmacy_id);
      const shortCode = ph?.shortCode ?? shortCodeFromName(ph?.name ?? s.pharmacy_id);
      const colorToken = ph?.colorToken ?? hashPharmacyColor(s.pharmacy_id);
      const driverMap = byDriver.get(s.driver_id) ?? new Map<string, CellRouteRef[]>();
      const arr = driverMap.get(s.shift_date) ?? [];
      arr.push({ routeId: s.id, pharmacyId: s.pharmacy_id, shortCode, colorToken });
      driverMap.set(s.shift_date, arr);
      byDriver.set(s.driver_id, driverMap);
      if (s.shift_date === today) {
        activeDriversToday.add(s.driver_id);
        const set = driversByPharmacyToday.get(s.pharmacy_id) ?? new Set<string>();
        set.add(s.driver_id);
        driversByPharmacyToday.set(s.pharmacy_id, set);
      }
    }

    // Stops-per-day is a workload concept — it stays route-driven.
    // A driver can be scheduled (has a shift) without a route yet;
    // that's the "assigned to work but nothing to deliver" state,
    // which reads as 0 stops for that day.
    for (const r of routes ?? []) {
      if (r.driver_id && r.pharmacy_id) {
        stopsPerDay.set(
          r.planned_for,
          (stopsPerDay.get(r.planned_for) ?? 0) + r.stops.length,
        );
      }
    }

    return { byDriver, stopsPerDay, activeDriversToday, driversByPharmacyToday };
  }, [shifts, routes, pharmacyById]);

  const sortedDrivers = useMemo(() => {
    if (!drivers) return [];
    return [...drivers]
      .map((d) => ({
        id: d.id,
        label: d.user?.full_name ?? d.user?.email ?? d.id.slice(0, 8),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [drivers]);

  // Per-pharmacy active-today summary for the stat strip + footer.
  const pharmacySummary = useMemo(() => {
    const entries: { name: string; shortCode: string; colorToken: PharmacyColor; count: number }[] = [];
    for (const [pharmacyId, set] of indexed.driversByPharmacyToday.entries()) {
      const ph = pharmacyById.get(pharmacyId);
      const name = ph?.name ?? pharmacyId;
      entries.push({
        name,
        shortCode: shortCodeFromName(name),
        colorToken: hashPharmacyColor(pharmacyId),
        count: set.size,
      });
    }
    entries.sort((a, b) => a.shortCode.localeCompare(b.shortCode));
    return entries;
  }, [indexed, pharmacyById]);

  const stopsTodayTotal = indexed.stopsPerDay.get(todayIso()) ?? 0;
  const activeTodayCount = indexed.activeDriversToday.size;
  const loading = shifts === null && !error;

  return (
    <div className="space-y-3">
      <HeaderBar
        weekStart={weekStart}
        weekEnd={weekEnd}
        activeToday={activeTodayCount}
        pharmacySummary={pharmacySummary}
        stopsToday={stopsTodayTotal}
        onPrev={() => onStartChange(addDays(weekStart, -7))}
        onNext={() => onStartChange(addDays(weekStart, 7))}
        onToday={() => onStartChange(mondayOf(todayIso()))}
      />

      {error && <Alert tone="error">{error}</Alert>}

      {migrationPending && (
        <div className="rounded-md border border-dashed border-border bg-bg-soft px-3 py-2 text-xs text-ink-3">
          Cell editing requires the <code className="font-mono">driver_shifts</code> migration
          (<code className="font-mono">20260629210000_driver_shifts.sql</code>) to be applied. Cells
          will stay read-only until then.
        </div>
      )}

      <ScheduleGrid
        days={days}
        drivers={sortedDrivers}
        cellsByDriver={indexed.byDriver}
        stopsPerDay={indexed.stopsPerDay}
        loading={loading}
        pharmacies={pharmacies ?? []}
        onCellChange={setCellPharmacy}
        savingCell={savingCell}
        readOnly={migrationPending}
      />

      <FooterLegend pharmacySummary={pharmacySummary} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header bar — date range + prev/today/next on the left, four inline
// stats on the right. Combines what used to be two separate rows
// (orphaned date label above a floating row of numbers).

function HeaderBar({
  weekStart,
  weekEnd,
  activeToday,
  pharmacySummary,
  stopsToday,
  onPrev,
  onNext,
  onToday,
}: {
  weekStart: string;
  weekEnd: string;
  activeToday: number;
  pharmacySummary: { name: string; shortCode: string; colorToken: PharmacyColor; count: number }[];
  stopsToday: number;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
}): JSX.Element {
  const top = pharmacySummary.slice(0, 2);
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 rounded-lg border border-border bg-bg px-4 py-2.5 shadow-card">
      <div className="flex items-center gap-3">
        <div className="text-sm font-semibold text-ink">{rangeLabel(weekStart, weekEnd)}</div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onPrev}
            aria-label="Previous two weeks"
            className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-border bg-bg text-ink-2 hover:bg-bg-hover"
          >
            <IconChevronLeft size={12} />
          </button>
          <button
            type="button"
            onClick={onToday}
            className="rounded-md border border-border bg-bg px-2 py-0.5 text-xs font-medium text-ink-2 hover:bg-bg-hover"
          >
            Today
          </button>
          <button
            type="button"
            onClick={onNext}
            aria-label="Next two weeks"
            className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-border bg-bg text-ink-2 hover:bg-bg-hover"
          >
            <IconChevronRight size={12} />
          </button>
        </div>
      </div>

      <div className="flex items-center gap-x-6">
        <Stat value={String(activeToday)} label="Active today" />
        <SplitStat entries={top} />
        <Stat value={String(stopsToday)} label="Stops today" />
        <Stat value="0" label="Time off" muted />
      </div>
    </div>
  );
}

function Stat({
  value,
  label,
  muted = false,
}: {
  value: string;
  label: string;
  muted?: boolean;
}): JSX.Element {
  return (
    <div className="flex flex-col items-center text-center">
      <div className={'text-2xl font-semibold leading-none ' + (muted ? 'text-ink-3' : 'text-ink')}>
        {value}
      </div>
      <div className="mt-1 text-[10px] uppercase tracking-wide text-ink-3">{label}</div>
    </div>
  );
}

function SplitStat({
  entries,
}: {
  entries: { shortCode: string; colorToken: PharmacyColor; count: number }[];
}): JSX.Element {
  if (entries.length === 0) {
    return <Stat value="0" label="Pharmacy split" muted />;
  }
  if (entries.length === 1) {
    const e = entries[0]!;
    return <Stat value={String(e.count)} label={e.shortCode} />;
  }
  const a = entries[0]!;
  const b = entries[1]!;
  return (
    <div className="flex flex-col items-center text-center">
      <div className="flex items-baseline gap-1.5 text-2xl font-semibold leading-none">
        <span className={CHIP_TEXT[a.colorToken]}>{a.count}</span>
        <span className="text-ink-3">/</span>
        <span className={CHIP_TEXT[b.colorToken]}>{b.count}</span>
      </div>
      <div className="mt-1 text-[10px] uppercase tracking-wide text-ink-3">
        {a.shortCode} / {b.shortCode}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Grid.

const CHIP_CLASSES: Record<PharmacyColor, string> = {
  emerald: 'bg-emerald-50 text-emerald-800 border border-emerald-200',
  sky: 'bg-blue-50 text-blue-800 border border-blue-200',
  amber: 'bg-amber-50 text-amber-800 border border-amber-200',
  violet: 'bg-violet-50 text-violet-800 border border-violet-200',
  slate: 'bg-slate-100 text-slate-700 border border-slate-200',
};

const CHIP_TEXT: Record<PharmacyColor, string> = {
  emerald: 'text-emerald-700',
  sky: 'text-blue-700',
  amber: 'text-amber-700',
  violet: 'text-violet-700',
  slate: 'text-slate-700',
};

function ScheduleGrid({
  days,
  drivers,
  cellsByDriver,
  stopsPerDay,
  loading,
  pharmacies,
  onCellChange,
  savingCell,
  readOnly = false,
}: {
  days: string[];
  drivers: { id: string; label: string }[];
  cellsByDriver: Map<string, Map<string, CellRouteRef[]>>;
  stopsPerDay: Map<string, number>;
  loading: boolean;
  pharmacies: PharmacyOption[];
  onCellChange: (driverId: string, shiftDate: string, pharmacyId: string | null) => void | Promise<void>;
  savingCell: string | null;
  readOnly?: boolean;
}): JSX.Element {
  const week1 = days.slice(0, 5);
  const week2 = days.slice(5, 10);
  // Visual: a single table is the cleanest grid behavior in CSS;
  // CNC's gap between weeks 1 and 2 is achieved with a separator
  // column.
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-bg shadow-card">
      <table className="min-w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-border bg-bg-soft">
            <th className="sticky left-0 z-10 w-36 bg-bg-soft px-2.5 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wide text-ink-3">
              Driver
            </th>
            {renderHeaderGroup(week1, stopsPerDay, 'Week 1')}
            <th className="w-2 border-l-2 border-border bg-bg-soft" aria-hidden />
            {renderHeaderGroup(week2, stopsPerDay, 'Week 2')}
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr>
              <td
                colSpan={1 + week1.length + 1 + week2.length}
                className="px-4 py-6 text-center text-sm text-ink-3"
              >
                Loading schedule…
              </td>
            </tr>
          )}
          {!loading && drivers.length === 0 && (
            <tr>
              <td
                colSpan={1 + week1.length + 1 + week2.length}
                className="px-4 py-6 text-center text-sm text-ink-3"
              >
                No drivers in this tenant yet.
              </td>
            </tr>
          )}
          {!loading &&
            drivers.map((driver) => {
              const driverDay = cellsByDriver.get(driver.id);
              return (
                <tr key={driver.id} className="border-b border-border last:border-b-0">
                  <td className="sticky left-0 z-10 truncate bg-bg px-2.5 py-1 text-xs font-medium text-ink">
                    {driver.label}
                  </td>
                  {week1.map((d) => (
                    <ScheduleCell
                      key={d}
                      cells={driverDay?.get(d)}
                      driverId={driver.id}
                      shiftDate={d}
                      pharmacies={pharmacies}
                      onChange={onCellChange}
                      saving={savingCell === `${driver.id}|${d}`}
                      readOnly={readOnly}
                    />
                  ))}
                  <td className="border-l-2 border-border bg-bg-soft" aria-hidden />
                  {week2.map((d) => (
                    <ScheduleCell
                      key={d}
                      cells={driverDay?.get(d)}
                      driverId={driver.id}
                      shiftDate={d}
                      pharmacies={pharmacies}
                      onChange={onCellChange}
                      saving={savingCell === `${driver.id}|${d}`}
                      readOnly={readOnly}
                    />
                  ))}
                </tr>
              );
            })}
        </tbody>
      </table>
    </div>
  );
}

function renderHeaderGroup(
  days: string[],
  stopsPerDay: Map<string, number>,
  _weekLabel: string,
): JSX.Element[] {
  return days.map((d) => {
    const count = stopsPerDay.get(d) ?? 0;
    return (
      <th
        key={d}
        className="border-l border-border px-1.5 py-1.5 text-center align-top text-[10px] font-semibold uppercase tracking-wide text-ink-3"
      >
        <div className="leading-tight">{shortDayLabel(d)}</div>
        <div className="leading-tight text-ink-2">{shortDateLabel(d)}</div>
        {count > 0 ? (
          <div className="mt-0.5 text-[10px] font-normal normal-case tracking-normal text-ink-3">
            {count}
          </div>
        ) : null}
      </th>
    );
  });
}

function ScheduleCell({
  cells,
  driverId,
  shiftDate,
  pharmacies,
  onChange,
  saving,
  readOnly = false,
}: {
  cells: CellRouteRef[] | undefined;
  driverId: string;
  shiftDate: string;
  pharmacies: PharmacyOption[];
  onChange: (driverId: string, shiftDate: string, pharmacyId: string | null) => void | Promise<void>;
  saving: boolean;
  readOnly?: boolean;
}): JSX.Element {
  const currentPharmacyId = cells?.[0]?.pharmacyId ?? '';
  const currentColor = cells?.[0]?.colorToken ?? 'slate';
  const isOff = !cells || cells.length === 0;
  // Wrap the visible chip in a native <select> for the picker. The
  // select is layered transparently over the chip so the chip is the
  // visible affordance; clicking anywhere on the cell opens the
  // dropdown. Native <select> beats a popover here: zero focus-trap
  // bugs, free keyboard handling, free mobile UX.
  return (
    <td
      className={
        'relative border-l border-border px-1.5 py-1 text-center align-middle ' +
        (saving ? 'opacity-50' : '')
      }
    >
      {isOff ? (
        <span className="text-[10px] font-medium text-ink-3">Off</span>
      ) : (
        <span
          className={
            'inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ' +
            CHIP_CLASSES[currentColor]
          }
        >
          {cells![0]!.shortCode}
        </span>
      )}
      {readOnly ? null : (
        <select
          aria-label={`Set shift for ${shiftDate}`}
          value={currentPharmacyId}
          disabled={saving}
          onChange={(e) => {
            const next = e.target.value;
            void onChange(driverId, shiftDate, next === '' ? null : next);
          }}
          className="absolute inset-0 cursor-pointer opacity-0"
        >
          <option value="">Off</option>
          {pharmacies.map((p) => (
            <option key={p.id} value={p.id}>
              {p.shortCode} — {p.name}
            </option>
          ))}
        </select>
      )}
    </td>
  );
}

// ---------------------------------------------------------------------------
// Footer legend / per-pharmacy summary.

function FooterLegend({
  pharmacySummary,
}: {
  pharmacySummary: { name: string; shortCode: string; colorToken: PharmacyColor; count: number }[];
}): JSX.Element | null {
  // Hide entirely when there's nothing to legend — the page reads
  // cleaner without an empty hint band.
  if (pharmacySummary.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-3 px-1 text-xs text-ink-3">
      {pharmacySummary.map((p) => (
        <span key={p.shortCode} className="inline-flex items-center gap-1.5">
          <span
            className={
              'inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ' +
              CHIP_CLASSES[p.colorToken]
            }
          >
            {p.shortCode}
          </span>
          <span>
            {p.name} · {p.count}
          </span>
        </span>
      ))}
    </div>
  );
}
